import { useEffect, useState } from "react";
import { api } from "@/lib/api";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import { Input } from "@/components/ui/input";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "@/components/ui/sheet";
import { ClipboardCheck, Download, History, Loader2, Mail, Percent, RefreshCw, Sparkles, TrendingUp } from "lucide-react";
import { toast } from "sonner";

function TrendsSection() {
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  useEffect(() => {
    if (show && !data) api.get("/hseq/audit-sampler/trends").then(({ data }) => setData(data)).catch(() => {});
  }, [show, data]);
  const rateCls = (r) => r == null ? "text-slate-400" : r >= 95 ? "text-emerald-700" : r >= 85 ? "text-amber-700" : "text-rose-700 font-semibold";
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button onClick={() => setShow(!show)} data-testid="task-audit-trends-toggle"
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-800">
        <span className="flex items-center gap-1.5"><TrendingUp className="w-4 h-4" /> Pass-rate trends (BU &amp; field worker)</span>
        <span className="text-xs text-slate-400">{show ? "Hide" : "Show"}</span>
      </button>
      {show && (
        <div className="px-4 pb-4 grid md:grid-cols-2 gap-4">
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Monthly pass rate by BU</div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
                <tr><th className="text-left py-1.5">Month</th><th className="text-left">BU</th>
                    <th className="text-right">Audits</th><th className="text-right">Fails</th>
                    <th className="text-right">Pass rate</th></tr>
              </thead>
              <tbody>
                {(data?.by_bu || []).slice(0, 24).map((r, i) => (
                  <tr key={i} className="border-b border-slate-100">
                    <td className="py-1">{r.period_month}</td><td>{r.bu}</td>
                    <td className="text-right tabular-nums">{r.audits}</td>
                    <td className={`text-right tabular-nums ${r.fails ? "text-rose-700" : "text-slate-400"}`}>{r.fails}</td>
                    <td className={`text-right tabular-nums ${rateCls(r.pass_rate)}`}>{r.pass_rate == null ? "—" : `${r.pass_rate}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          <div>
            <div className="text-[10px] uppercase tracking-wider text-slate-500 mb-1.5">Field workers — most fails first</div>
            <table className="w-full text-xs">
              <thead className="text-[10px] uppercase text-slate-500 border-b border-slate-200">
                <tr><th className="text-left py-1.5">Worker</th><th className="text-left">BU</th>
                    <th className="text-right">Audits</th><th className="text-right">Fails</th>
                    <th className="text-right">Pass rate</th></tr>
              </thead>
              <tbody>
                {(data?.by_employee || []).slice(0, 12).map((e, i) => (
                  <tr key={i} className="border-b border-slate-100" data-testid={`trend-emp-${i}`}>
                    <td className="py-1 font-medium text-slate-800">{e.employee_name}</td><td>{e.bu}</td>
                    <td className="text-right tabular-nums">{e.audits}</td>
                    <td className={`text-right tabular-nums ${e.fails ? "text-rose-700 font-semibold" : "text-slate-400"}`}>{e.fails}</td>
                    <td className={`text-right tabular-nums ${rateCls(e.pass_rate)}`}>{e.pass_rate == null ? "—" : `${e.pass_rate}%`}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
      )}
    </div>
  );
}

function SummaryConfigCard() {
  const [cfg, setCfg] = useState(null);
  const [emails, setEmails] = useState("");
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    api.get("/hseq/audit-sampler/summary-config")
      .then(({ data }) => { setCfg(data); setEmails((data.recipients || []).join(", ")); })
      .catch(() => {});
  }, []);
  if (!cfg) return null;
  const parse = () => emails.split(/[,;\s]+/).map((s) => s.trim()).filter(Boolean);
  const save = async () => {
    setBusy(true);
    try {
      const { data } = await api.put("/hseq/audit-sampler/summary-config", { enabled: cfg.enabled, recipients: parse() });
      setCfg(data);
      toast.success("Weekly summary recipients saved");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  const sendNow = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/hseq/audit-sampler/summary/send-now");
      toast.success(`Summary sent to ${data.ok_count} recipient(s)`);
    } catch (e) { toast.error(e.response?.data?.detail || "Send failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-between gap-3 flex-wrap">
      <div>
        <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
          <Mail className="w-4 h-4" /> Weekly leadership summary <span className="text-[10px] text-slate-400">(Mondays)</span>
        </div>
        <div className="text-[11px] text-slate-500">
          Individual failures go to the relevant BU manager. This weekly report goes to:
          {" "}{(cfg.effective_recipients || []).join(", ") || "—"}
        </div>
      </div>
      <div className="flex items-center gap-2 flex-wrap">
        <Input value={emails} onChange={(e) => setEmails(e.target.value)}
               placeholder="exec@…, ops@…, hseq@… (blank = all executives)"
               data-testid="audit-summary-recipients" className="h-8 text-xs w-80" />
        <Button size="sm" variant="outline" onClick={save} disabled={busy} data-testid="audit-summary-save">Save</Button>
        <Button size="sm" variant="outline" onClick={sendNow} disabled={busy} data-testid="audit-summary-send-now">
          Send now
        </Button>
      </div>
    </div>
  );
}

function SampleRatesCard() {
  const [data, setData] = useState(null);
  const [show, setShow] = useState(false);
  const [defaultPct, setDefaultPct] = useState("10");
  const [perBu, setPerBu] = useState({});
  const [busy, setBusy] = useState(false);
  useEffect(() => {
    if (show && !data) {
      api.get("/hseq/audit-sampler/sample-rates").then(({ data }) => {
        setData(data);
        setDefaultPct(String(data.default_pct));
        const m = {};
        (data.bus || []).forEach((b) => { if (b.rate_pct != null) m[b.code] = String(b.rate_pct); });
        setPerBu(m);
      }).catch(() => {});
    }
  }, [show, data]);
  const save = async () => {
    setBusy(true);
    try {
      const per_bu = {};
      Object.entries(perBu).forEach(([k, v]) => {
        const n = parseFloat(v);
        if (v !== "" && !isNaN(n)) per_bu[k] = n;
      });
      const { data: d } = await api.put("/hseq/audit-sampler/sample-rates",
        { default_pct: parseFloat(defaultPct) || 10, per_bu });
      setData(d);
      toast.success("Sample rates saved — use Re-draw sample to apply them to the current month");
    } catch (e) { toast.error(e.response?.data?.detail || "Save failed"); }
    finally { setBusy(false); }
  };
  return (
    <div className="rounded-lg border border-slate-200 bg-white">
      <button onClick={() => setShow(!show)} data-testid="sample-rates-toggle"
              className="w-full flex items-center justify-between px-4 py-3 text-sm font-semibold text-slate-800">
        <span className="flex items-center gap-1.5"><Percent className="w-4 h-4" /> Audit sample rates per BU
          <span className="text-[10px] font-normal text-slate-400">(executive)</span></span>
        <span className="text-xs text-slate-400">{show ? "Hide" : "Show"}</span>
      </button>
      {show && !data && <div className="px-4 pb-4 text-xs text-slate-400"><Loader2 className="w-3.5 h-3.5 animate-spin inline mr-1.5" />Loading…</div>}
      {show && data && (
        <div className="px-4 pb-4 space-y-3">
          <div className="text-[11px] text-slate-500">
            Set a higher rate for BUs with low-volume/high-cost work and a lower rate for high-volume/low-cost work.
            Blank = use the default. Changes apply to future sample generations — press <span className="font-semibold">Re-draw sample</span> to apply to the current month (completed audits are kept).
          </div>
          <div className="flex items-center gap-2">
            <span className="text-xs font-medium text-slate-700 w-44">Default rate</span>
            <Input type="number" min="1" max="100" step="0.5" value={defaultPct}
                   onChange={(e) => setDefaultPct(e.target.value)} disabled={!data.can_edit}
                   data-testid="sample-rate-default" className="h-7 w-20 text-xs" />
            <span className="text-xs text-slate-400">%</span>
          </div>
          <div className="grid sm:grid-cols-2 gap-x-6 gap-y-1.5">
            {(data.bus || []).map((b) => (
              <div key={b.code} className="flex items-center gap-2">
                <span className="text-xs text-slate-600 w-44 truncate" title={b.name}>{b.code} <span className="text-slate-400">{b.name}</span></span>
                <Input type="number" min="1" max="100" step="0.5"
                       value={perBu[b.code] ?? ""} placeholder={String(data.default_pct)}
                       onChange={(e) => setPerBu((m) => ({ ...m, [b.code]: e.target.value }))}
                       disabled={!data.can_edit}
                       data-testid={`sample-rate-${b.code}`} className="h-7 w-20 text-xs" />
                <span className="text-xs text-slate-400">%</span>
              </div>
            ))}
          </div>
          {data.can_edit ? (
            <Button size="sm" onClick={save} disabled={busy} data-testid="sample-rates-save"
                    className="bg-gbe hover:bg-gbe/90">
              {busy ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : null}Save rates
            </Button>
          ) : (
            <div className="text-[11px] text-slate-400">Only executives can change these rates.</div>
          )}
        </div>
      )}
    </div>
  );
}

function WorkerHistory({ name, currentRef }) {
  const [data, setData] = useState(null);
  useEffect(() => {
    setData(null);
    if (name) api.get("/hseq/audit-sampler/worker-history", { params: { employee_name: name } })
      .then(({ data }) => setData(data)).catch(() => setData({ history: [] }));
  }, [name]);
  const rows = (data?.history || []).filter((h) => h.ref_no !== currentRef);
  if (!data) return <div className="text-[11px] text-slate-400 flex items-center gap-1.5"><Loader2 className="w-3 h-3 animate-spin" /> Loading history…</div>;
  return (
    <div data-testid="audit-worker-history">
      <div className="text-xs font-medium text-slate-700 mb-1.5 flex items-center gap-1.5">
        <History className="w-3.5 h-3.5" /> {name}'s audit history
        {data.audits > 0 && (
          <span className={`text-[10px] font-normal ${data.fails ? "text-rose-700 font-semibold" : "text-slate-400"}`}>
            {data.fails} fail{data.fails === 1 ? "" : "s"} in last {data.audits} audit{data.audits === 1 ? "" : "s"}
          </span>
        )}
      </div>
      {rows.length === 0 ? (
        <div className="text-[11px] text-slate-400">No previous audits on record.</div>
      ) : (
        <div className="rounded-md border border-slate-200 divide-y divide-slate-100 max-h-44 overflow-y-auto">
          {rows.map((h, i) => (
            <div key={i} className={`px-2.5 py-1.5 text-[11px] flex items-start gap-2 ${h.result === "fail" ? "bg-rose-50/60" : ""}`}
                 data-testid={`worker-history-row-${i}`}>
              <span className={`mt-px inline-block px-1.5 py-px rounded text-[9px] font-semibold uppercase ${
                h.result === "fail" ? "bg-rose-100 text-rose-800"
                : h.result === "na" ? "bg-slate-100 text-slate-500" : "bg-emerald-100 text-emerald-800"}`}>
                {h.result === "na" ? "N/A" : h.result}
              </span>
              <span className="flex-1 min-w-0">
                <span className="text-slate-600">{h.period_month}</span>
                {" · "}<span className="font-mono text-[10px] text-slate-700">{h.ref_no}</span>
                {h.jsa_result && <span className="text-slate-500"> · JSA {h.jsa_result}</span>}
                {h.ccew_result && <span className="text-slate-500"> · CCEW {h.ccew_result}</span>}
                {h.comments && <span className="block text-slate-400 truncate" title={h.comments}>{h.comments}</span>}
              </span>
            </div>
          ))}
        </div>
      )}
    </div>
  );
}

const YN = ({ value, onChange, options = ["yes", "no"], testid, disabled }) => (
  <div className={`inline-flex rounded-md border border-slate-200 overflow-hidden text-xs ${disabled ? "opacity-40 pointer-events-none" : ""}`}>
    {options.map((o) => (
      <button key={o} type="button" data-testid={`${testid}-${o}`}
              onClick={() => onChange(o)}
              className={`px-3 py-1.5 capitalize border-r border-slate-200 last:border-r-0 transition ${
                value === o
                  ? o === "no" ? "bg-rose-600 text-white" : "bg-gbe text-white"
                  : "bg-white text-slate-600 hover:bg-slate-50"}`}>
        {o === "na" ? "N/A" : o}
      </button>
    ))}
  </div>
);

export function TaskAuditsTab({ canManage, isExecutive }) {
  const [months, setMonths] = useState([]);
  const [month, setMonth] = useState("");
  const [summary, setSummary] = useState(null);
  const [samples, setSamples] = useState([]);
  const [buFilter, setBuFilter] = useState("all");
  const [busy, setBusy] = useState(false);
  const [generating, setGenerating] = useState(false);
  const [open, setOpen] = useState(null);       // sample being audited
  const [form, setForm] = useState({});
  const [saving, setSaving] = useState(false);
  const [exporting, setExporting] = useState(false);

  const exportXlsx = async () => {
    setExporting(true);
    try {
      const r = await api.get("/hseq/audit-sampler/export.xlsx", {
        params: { period_month: month, ...(buFilter !== "all" ? { bu: buFilter } : {}) },
        responseType: "blob",
      });
      const url = window.URL.createObjectURL(new Blob([r.data]));
      const a = document.createElement("a");
      a.href = url;
      a.download = `task-audits-${month}${buFilter !== "all" ? `-${buFilter}` : ""}.xlsx`;
      a.click();
      window.URL.revokeObjectURL(url);
    } catch (e) { toast.error("Export failed"); }
    finally { setExporting(false); }
  };

  const loadMonths = async () => {
    try {
      const { data } = await api.get("/hseq/audit-sampler/months");
      setMonths(data.months || []);
      if (!month && data.months?.length) setMonth(data.months[0].period_month);
    } catch { /* ignore */ }
  };
  const load = async () => {
    if (!month) return;
    setBusy(true);
    try {
      const [{ data: sum }, { data: sam }] = await Promise.all([
        api.get("/hseq/audit-sampler/summary", { params: { period_month: month } }),
        api.get("/hseq/audit-sampler/samples", { params: { period_month: month, ...(buFilter !== "all" ? { bu: buFilter } : {}) } }),
      ]);
      setSummary(sum);
      setSamples(sam.samples || []);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Failed to load task audits");
    } finally { setBusy(false); }
  };
  useEffect(() => { loadMonths(); /* eslint-disable-next-line */ }, []);
  useEffect(() => { load(); /* eslint-disable-next-line */ }, [month, buFilter]);

  const generate = async (force = false) => {
    if (force && !window.confirm(
      "Re-draw this month's sample from the latest Completed Tasks import?\n\nPending (un-audited) tasks are replaced with a fresh 10% draw using the Lab Resource field. Completed audits are kept and relinked to the labour person.")) return;
    setGenerating(true);
    try {
      const { data } = await api.post("/hseq/audit-sampler/generate", { period_month: month, force });
      if (data.started) toast.success(data.note || "Sample generation started — refresh in a few minutes");
      else if (data.ok) toast.info(data.reason || "Generation already running");
      else toast.warning(data.reason || "Nothing to sample");
      setTimeout(() => { load(); loadMonths(); }, 4000);
    } catch (e) {
      toast.error(e.response?.data?.detail || "Generate failed");
    } finally { setGenerating(false); }
  };

  const openAudit = (s) => {
    setOpen(s);
    setForm({
      jsa_carried_out: s.jsa_carried_out, jsa_correct: s.jsa_correct,
      ccew_required: s.ccew_required, ccew_completed: s.ccew_completed,
      comments: s.comments || "",
    });
  };
  const save = async () => {
    setSaving(true);
    try {
      const { data } = await api.patch(`/hseq/audit-sampler/samples/${open.id}`, form);
      toast.success(data.status === "audited" ? `Audit recorded — ${data.result?.toUpperCase()}` : "Progress saved");
      setOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Save failed");
    } finally { setSaving(false); }
  };

  const cancelAudit = async () => {
    const reason = window.prompt(
      `Cancel the audit for ${open.employee_name} on ${(open.work_date || open.completed_date || "").slice(0, 10)}?\n\nReason (optional):`);
    if (reason === null) return;
    setSaving(true);
    try {
      await api.post(`/hseq/audit-sampler/samples/${open.id}/cancel`, { reason });
      toast.success("Audit cancelled — excluded from completion stats");
      setOpen(null);
      load();
    } catch (e) {
      toast.error(e.response?.data?.detail || "Cancel failed");
    } finally { setSaving(false); }
  };

  const monthGenerated = months.find((m) => m.period_month === month)?.sample_generated;
  const bus = summary?.bus || [];

  return (
    <div className="space-y-4">
      <div className="rounded-lg border border-slate-200 bg-white p-4 flex items-center justify-between gap-2 flex-wrap">
        <div>
          <div className="text-sm font-semibold text-slate-800 flex items-center gap-1.5">
            <ClipboardCheck className="w-4 h-4" /> Task JSA &amp; CCEW audits
          </div>
          <div className="text-[11px] text-slate-500">
            A sample of worker-days per BU each month (10% default, rate configurable per BU below) — each audit covers one labour resource on one day of work. Check JSAs are done
            correctly (no shortcuts) and CCEW forms completed where required.
          </div>
        </div>
        <div className="flex items-center gap-2 flex-wrap">
          <Select value={month} onValueChange={setMonth}>
            <SelectTrigger data-testid="task-audit-month" className="h-8 w-32 text-xs"><SelectValue placeholder="Month" /></SelectTrigger>
            <SelectContent>
              {months.map((m) => (
                <SelectItem key={m.period_month} value={m.period_month}>
                  {m.period_month}{m.sample_generated ? "" : " (no sample)"}
                </SelectItem>
              ))}
            </SelectContent>
          </Select>
          <Select value={buFilter} onValueChange={setBuFilter}>
            <SelectTrigger data-testid="task-audit-bu-filter" className="h-8 w-36 text-xs"><SelectValue /></SelectTrigger>
            <SelectContent>
              <SelectItem value="all">All BUs</SelectItem>
              {bus.map((b) => <SelectItem key={b.bu_code} value={b.bu_code}>{b.bu_code} {b.bu_name || ""}</SelectItem>)}
            </SelectContent>
          </Select>
          {canManage && month && !monthGenerated && (
            <Button size="sm" onClick={() => generate(false)} disabled={generating} data-testid="task-audit-generate"
                    className="bg-gbe hover:bg-gbe/90">
              {generating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Generate 10% sample
            </Button>
          )}
          {canManage && month && monthGenerated && (
            <Button size="sm" variant="outline" onClick={() => generate(true)} disabled={generating}
                    data-testid="task-audit-redraw">
              {generating ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Sparkles className="w-3.5 h-3.5 mr-1" />}
              Re-draw sample
            </Button>
          )}
          {canManage && month && monthGenerated && (
            <Button size="sm" variant="outline" onClick={exportXlsx} disabled={exporting}
                    data-testid="task-audit-export">
              {exporting ? <Loader2 className="w-3.5 h-3.5 mr-1 animate-spin" /> : <Download className="w-3.5 h-3.5 mr-1" />}
              Export Excel
            </Button>
          )}
          <Button size="sm" variant="outline" onClick={load} disabled={busy} data-testid="task-audit-refresh">
            {busy ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <RefreshCw className="w-3.5 h-3.5" />}
          </Button>
        </div>
      </div>

      {bus.length > 0 && (
        <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
          {bus.map((b) => (
            <div key={b.bu_code} data-testid={`task-audit-bu-card-${b.bu_code}`}
                 className="rounded-lg border border-slate-200 bg-white p-3">
              <div className="text-[10px] uppercase tracking-wider text-slate-500">{b.bu_code} {b.bu_name || ""}</div>
              <div className="text-lg font-semibold text-slate-900 mt-0.5">{b.audited}/{b.sampled} <span className="text-xs font-normal text-slate-500">audited</span></div>
              <div className="text-[11px] text-slate-500">{b.sampled} sampled of {b.population} worker-days
                {b.failed > 0 && <span className="text-rose-700 font-semibold"> · {b.failed} fail</span>}
              </div>
            </div>
          ))}
        </div>
      )}

      <div className="rounded-lg border border-slate-200 bg-white overflow-hidden">
        {samples.length === 0 && !busy ? (
          <div className="text-xs text-slate-400 text-center py-10">
            {month ? (monthGenerated ? "No samples for this filter." : "No sample generated yet for this month — it generates automatically when the Completed Tasks feed arrives.") : "No completed-task data yet. IT sends the monthly Completed Tasks export to the Import Hub."}
          </div>
        ) : (
          <table className="w-full text-xs">
            <thead className="bg-slate-50 text-[10px] uppercase tracking-wider text-slate-500 border-b border-slate-200">
              <tr>
                <th className="text-left px-3 py-2">Ref No</th>
                <th className="text-left px-3 py-2">BU</th>
                <th className="text-left px-3 py-2">Field worker</th>
                <th className="text-left px-3 py-2">Completed</th>
                <th className="text-left px-3 py-2">Task type</th>
                <th className="text-left px-3 py-2">Status</th>
                <th className="text-right px-3 py-2"></th>
              </tr>
            </thead>
            <tbody>
              {samples.map((s) => (
                <tr key={s.id} className="border-b border-slate-100 hover:bg-slate-50/50">
                  <td className="px-3 py-1.5 font-mono text-[11px] text-slate-700">
                    {s.ref_no}
                    {(s.jobs_count || 0) > 1 && (
                      <span className="ml-1 text-[9px] text-slate-400" title={(s.ref_nos || []).join(", ")}>
                        {s.jobs_count} jobs
                      </span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-slate-600">{s.bu_code}</td>
                  <td className="px-3 py-1.5 font-medium text-slate-800">
                    {s.employee_name}
                    {s.employee_source === "labour_column" || s.employee_source === "labour_entry" ? (
                      <span className="ml-1 text-[9px] text-emerald-700" title={`Labour entry${s.completed_by ? ` — task closed by ${s.completed_by}` : ""}`}>labour</span>
                    ) : (
                      <span className="ml-1 text-[9px] text-slate-400" title="No labour entry found — using who closed the task">closed-by</span>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-slate-500">{(s.completed_date || "").slice(0, 10)}</td>
                  <td className="px-3 py-1.5 text-slate-500">{s.task_type || "—"}</td>
                  <td className="px-3 py-1.5">
                    {s.status === "audited" ? (
                      <Badge className={s.result === "pass" ? "bg-emerald-100 text-emerald-800 border-emerald-200"
                                        : s.result === "na" ? "bg-slate-100 text-slate-600 border-slate-200"
                                        : "bg-rose-100 text-rose-800 border-rose-200"}>
                        {s.result === "pass" ? "Pass" : s.result === "na" ? "N/A" : "Fail"}
                      </Badge>
                    ) : s.status === "cancelled" ? (
                      <Badge variant="outline" className="text-slate-400 line-through" title={s.cancel_reason || ""}>Cancelled</Badge>
                    ) : (
                      <Badge variant="outline" className="text-slate-500">Pending</Badge>
                    )}
                  </td>
                  <td className="px-3 py-1.5 text-right">
                    {canManage && s.status !== "cancelled" && (
                      <Button size="sm" variant="outline" className="h-6 text-[11px]"
                              data-testid={`task-audit-open-${s.ref_no}`}
                              onClick={() => openAudit(s)}>
                        {s.status === "audited" ? "Review" : "Audit"}
                      </Button>
                    )}
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        )}
      </div>

      <TrendsSection />
      {canManage && <SampleRatesCard />}
      {canManage && <SummaryConfigCard />}

      <Sheet open={!!open} onOpenChange={(o) => { if (!o) setOpen(null); }}>
        <SheetContent className="w-full sm:max-w-md overflow-y-auto">
          {open && (
            <>
              <SheetHeader>
                <SheetTitle className="text-base">Audit {open.employee_name} — {(open.work_date || open.completed_date || "").slice(0, 10)}</SheetTitle>
                <SheetDescription className="text-xs">
                  {open.bu_code}{open.task_type ? ` · ${open.task_type}` : ""} · one worker, one day of work
                  {(open.ref_nos || [open.ref_no]).filter(Boolean).length > 0 && (
                    <span className="block mt-1 font-mono text-[10px] text-slate-500">
                      Jobs: {(open.ref_nos && open.ref_nos.length ? open.ref_nos : [open.ref_no]).join(", ")}
                    </span>
                  )}
                </SheetDescription>
              </SheetHeader>
              <div className="space-y-5 mt-5">
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1.5">1. JSA carried out for this task? <span className="text-slate-400">(N/A = task didn't require a JSA)</span></div>
                  <YN value={form.jsa_carried_out} options={["yes", "no", "na"]}
                      onChange={(v) => setForm((f) => ({ ...f, jsa_carried_out: v, ...(v !== "yes" ? { jsa_correct: null } : {}) }))}
                      testid="audit-jsa-carried" />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1.5">
                    2. JSA completed correctly — no shortcuts?
                    {form.jsa_carried_out !== "yes" && <span className="text-slate-400 font-normal"> (only applies when Q1 = Yes)</span>}
                  </div>
                  <YN value={form.jsa_correct} disabled={form.jsa_carried_out !== "yes"}
                      onChange={(v) => setForm((f) => ({ ...f, jsa_correct: v }))} testid="audit-jsa-correct" />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1.5">3. Does this job require a CCEW compliance form?</div>
                  <YN value={form.ccew_required}
                      onChange={(v) => setForm((f) => ({ ...f, ccew_required: v, ...(v !== "yes" ? { ccew_completed: null } : {}) }))}
                      testid="audit-ccew-required" />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1.5">
                    4. CCEW form completed?
                    {form.ccew_required !== "yes" && <span className="text-slate-400 font-normal"> (only applies when Q3 = Yes)</span>}
                  </div>
                  <YN value={form.ccew_completed} disabled={form.ccew_required !== "yes"}
                      onChange={(v) => setForm((f) => ({ ...f, ccew_completed: v }))} testid="audit-ccew-completed" />
                </div>
                <div>
                  <div className="text-xs font-medium text-slate-700 mb-1.5">Comments</div>
                  <Textarea value={form.comments} onChange={(e) => setForm((f) => ({ ...f, comments: e.target.value }))}
                            data-testid="audit-comments" rows={3} className="text-xs"
                            placeholder="Notes, shortcuts observed, follow-up required…" />
                </div>
                <WorkerHistory name={open.employee_name} currentRef={open.ref_no} />
                <Button onClick={save} disabled={saving} className="w-full bg-gbe hover:bg-gbe/90"
                        data-testid="audit-save">
                  {saving ? <Loader2 className="w-4 h-4 mr-1.5 animate-spin" /> : null}
                  Save audit
                </Button>
                {isExecutive && open.status !== "audited" && (
                  <Button variant="outline" onClick={cancelAudit} disabled={saving}
                          className="w-full text-rose-700 border-rose-200 hover:bg-rose-50"
                          data-testid="audit-cancel">
                    Cancel this audit
                  </Button>
                )}
              </div>
            </>
          )}
        </SheetContent>
      </Sheet>
    </div>
  );
}
